<template>
  <!--
    A word on a handshake already made. The same card the rest of the app asks
    its short questions on, with a line to write on where the question would be.
  -->
  <dialog ref="dialogRef" class="note-dialog" @close="emit('close')" @click="handleBackdropClick">
    <form class="note-card" @submit.prevent="save">
      <div class="note-title">{{ localize('handshake.note.title') }}</div>

      <input
        v-model="draft"
        type="text"
        :maxlength="HANDSHAKE_NOTE_MAX_LENGTH"
        :placeholder="localize('handshake.note.placeholder')"
        autocomplete="off"
        spellcheck="false"
        class="note-input"
      />

      <div class="note-actions">
        <!--
          Opening a dialog puts the focus somewhere, and left to itself it
          lands on the line — which on a phone throws the keyboard up over the
          card before anyone has asked for it. Naming the button instead keeps
          the focus on something that does not type.
        -->
        <button type="submit" class="note-save" autofocus>{{ localize('handshake.note.save') }}</button>

        <!-- Rubbing a note out is the same errand as writing one, so it is here
             rather than somewhere else, and only where there is one to rub. -->
        <button v-if="note" type="button" class="note-clear" @click="clear">
          {{ localize('handshake.note.clear') }}
        </button>
      </div>

      <div class="note-close" @click="emit('close')">
        <GamIcon :name="GamIconName.X_CLOSE" size="24px" is-current />
      </div>
    </form>
  </dialog>
</template>

<script lang="ts" setup>
import { localize } from '@/core/gambits';
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { HANDSHAKE_NOTE_MAX_LENGTH, tidyHandshakeNote } from '@/views/main/handshakes/utils/handshake-note';
import { onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps<{ note: string }>();

const emit = defineEmits<{ close: []; save: [note: string] }>();

const dialogRef = ref<HTMLDialogElement>();
const draft = ref(props.note);

onMounted(() => {
  dialogRef.value?.showModal();
});

onBeforeUnmount(() => {
  if (dialogRef.value?.open) dialogRef.value.close();
});

const handleBackdropClick = (e: MouseEvent) => {
  if (e.target === dialogRef.value) emit('close');
};

const save = () => emit('save', tidyHandshakeNote(draft.value));

const clear = () => emit('save', '');
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

.note-dialog {
  width: 100%;
  height: 100%;
  border: 0;
  padding: var(--sai-top) 0 var(--sai-bottom);
  margin: 0;
  max-width: 100%;
  max-height: 100%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  overflow: hidden;

  &::backdrop {
    background: rgba(23, 23, 23, 0.75);
  }
}

.note-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--spacing-xxl) var(--spacing-xl);
  padding-bottom: 6.5rem;
  background: var(--color-dark-700);
  border: 1px solid var(--color-white-10);
  border-radius: var(--radius-large);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  max-width: 300px;
  width: 100%;
}

.note-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-white);
  margin-bottom: var(--spacing-xl);
}

/* A line to write on, the way the sheet asks for the same word. */
.note-input {
  width: 100%;
  padding: 0 0 var(--spacing-s);
  margin-bottom: var(--spacing-xl);
  font-family: inherit;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
  color: var(--color-white);
  background: transparent;
  border: none;
  border-bottom: 1px solid var(--color-white-20);
  outline: none;
  caret-color: var(--color-white);

  &::placeholder {
    color: var(--color-white-30);
    font-weight: 500;
  }

  &:focus {
    border-bottom-color: var(--color-white-50);
  }
}

.note-actions {
  margin-top: auto;
  width: 100%;
}

.note-save {
  width: 100%;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 2rem;
  background: var(--color-linear-gradient);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 200ms ease;

  @include hover {
    opacity: 0.9;
  }
}

.note-clear {
  margin-top: var(--spacing-md);
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 2rem;
  background: transparent;
  color: var(--color-white-70);
  font-size: 15px;
  font-weight: 600;
  width: 100%;
  cursor: pointer;
  transition: all 200ms ease;

  @include hover {
    background: var(--color-white-5);
  }
}

.note-close {
  position: absolute;
  bottom: var(--safe-bottom-offset);
  left: 50%;
  transform: translateX(-50%);
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: rgba(0, 0, 0, 0.2);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  touch-action: manipulation;
  transition: all 200ms linear;

  @include hover {
    color: var(--color-white);
  }
}
</style>
